import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Map from './Map';

const { FiMapPin, FiPhone, FiMail, FiSend, FiCheckCircle } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({ isSubmitting: false, isSubmitted: false, error: 'Please fill in all required fields.' });
      return;
    }

    const subject = `SSC Contact: ${formData.subject} - ${formData.name}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`;
    const mailtoUrl = `mailto:info@southernsaviours.cc?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      window.open(mailtoUrl, '_blank');
      setTimeout(() => {
        setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 1000);
    } catch (error) {
      setFormStatus({ isSubmitting: false, isSubmitted: false, error: 'Failed to open email client.' });
    }
  };

  const contactInfo = [
    { icon: FiMapPin, title: 'Our Sanctuary', details: ['14 Ada-George Road', 'Port-Harcourt City, Rivers State'] },
    { icon: FiPhone, title: 'Call Us', details: ['+234 906 424 6172', '+234 909 370 8465'] },
    { icon: FiMail, title: 'Email Address', details: ['info@southernsaviours.cc'] }
  ];

  return (
    <section id="contact" className="py-24 bg-gray-50 dark:bg-gray-950 transition-colors duration-300 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-blue-600 font-bold tracking-widest uppercase text-sm mb-4 block">Connect With Us</span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6">
            We'd Love to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-500">Hear From You</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Whether you have a prayer request, a question, or want to join a ministry, our team is here for you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Info & Map Container */}
          <div className="flex flex-col space-y-8 h-full">
            <div className="grid sm:grid-cols-3 gap-6">
              {contactInfo.map((info, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 text-center"
                >
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={info.icon} className="text-blue-600 dark:text-blue-400 w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">{info.title}</h4>
                  {info.details.map((d, i) => (
                    <p key={i} className="text-xs text-gray-600 dark:text-gray-400">{d}</p>
                  ))}
                </motion.div>
              ))}
            </div>

            {/* Map with defined height to prevent overlap */}
            <div className="flex-grow">
              <Map />
            </div>
          </div>

          {/* Form Container */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-900 p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 h-full"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Send a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Name</label>
                  <input 
                    type="text" name="name" value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-600 dark:text-white transition-all"
                    placeholder="John Doe" required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  <input 
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-600 dark:text-white transition-all"
                    placeholder="john@example.com" required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Subject</label>
                <select 
                  name="subject" value={formData.subject} onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-600 dark:text-white transition-all"
                  required
                >
                  <option value="">Select a topic</option>
                  <option value="Prayer Request">Prayer Request</option>
                  <option value="Testimony">Testimony</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Marriage University">Marriage University</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Message</label>
                <textarea 
                  name="message" value={formData.message} onChange={handleChange} rows="5"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-600 dark:text-white transition-all"
                  placeholder="How can we help you?" required
                ></textarea>
              </div>

              <button 
                type="submit" 
                disabled={formStatus.isSubmitting}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/30 transition-all flex items-center justify-center space-x-2"
              >
                <span>{formStatus.isSubmitting ? 'Sending...' : 'Send Message'}</span>
                {!formStatus.isSubmitting && <SafeIcon icon={FiSend} className="w-5 h-5" />}
              </button>

              {formStatus.isSubmitted && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl flex items-center space-x-2">
                  <SafeIcon icon={FiCheckCircle} />
                  <span>Success! Your email client has been opened.</span>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;